// lib/microsite/shippingCheckout.ts
//
// Lógica PURA del checkout en modo "Envío" (Fase 1b T11). Sin React, sin
// fetch: solo lo que decide qué se manda a `/api/shipping/quote`, cómo se lee
// su respuesta, y cuándo el quote que el cliente tiene en mano deja de ser
// válido. Vive aparte de los componentes por el mismo motivo que
// `couponUx.ts` — el repo no tiene infra de tests de componentes (ver
// `components/microsite/sheet/__tests__/cartStepCoupon.markup.test.tsx`), así
// que la máquina de estados/cliente de cotización se extrae acá para poder
// probarla con jest normal, sin jsdom.
//
// Estructural a propósito (como `lib/delivery/formatDropoff.ts`): acepta
// cualquier objeto con estos campos, sin que `lib/` importe tipos de
// `components/`.

import { shipErrorKindFromResponse, type ShipErrorKind } from "@/components/microsite/sheet/shipErrorUx"

export interface CartLineLike {
  itemId: string
  quantity: number
}

/** Espeja `ShipQuote` de components/microsite/types.ts — ver ese archivo. */
export interface ShipQuoteLike {
  quoteId: string
  amountCents: number
  carrier: string | null
  service: string | null
  expiresAt: string
  freeShippingApplied: boolean
}

export type ShipQuoteResult =
  | { ok: true; quote: ShipQuoteLike }
  | { ok: false; kind: ShipErrorKind; minParcels?: number }

/**
 * Carrito → `items` del body de `/api/shipping/quote`. Consolida líneas
 * repetidas del MISMO itemId (agregado en dos momentos, con modificadores
 * distintos) sumando qty — el body schema del route RECHAZA (400) un itemId
 * duplicado, y el carrito del microsite sí puede traer dos líneas del mismo
 * producto (`cartId` distinto, `itemId` igual). Mismo criterio que
 * `/api/online-orders/session` usa para el mismo problema del lado shipping
 * (IMPORTANT-6 de esa ruta).
 */
export function buildShipQuoteItems(
  cart: CartLineLike[],
): Array<{ itemId: string; qty: number }> {
  const byId = new Map<string, number>()
  for (const line of cart) {
    byId.set(line.itemId, (byId.get(line.itemId) ?? 0) + line.quantity)
  }
  return [...byId.entries()].map(([itemId, qty]) => ({ itemId, qty }))
}

/**
 * Huella del carrito PARA la invalidación del quote local (no confundir con
 * `lib/shipping/cartFingerprint.ts`, que es server-side y usa peso/medidas —
 * acá solo hace falta detectar "el carrito cambió", no reproducir la huella
 * server-trusted). Determinística: mismo carrito ⇒ misma cadena, sin importar
 * el orden en que se agregaron las líneas.
 */
export function shipCartFingerprint(cart: CartLineLike[]): string {
  return buildShipQuoteItems(cart)
    .map((i) => `${i.itemId}:${i.qty}`)
    .sort()
    .join(",")
}

/**
 * status + body de `/api/shipping/quote` → resultado tipado. Única lectura de
 * la respuesta — ni el componente ni PaymentStep deberían volver a mirar
 * `res.status`/`body.error` a mano.
 */
export function parseShipQuoteResponse(status: number, body: unknown): ShipQuoteResult {
  const b = (body ?? null) as { quote?: ShipQuoteLike; error?: string; minParcels?: number } | null
  if (status === 200 && b?.quote && typeof b.quote.quoteId === "string" && b.quote.quoteId.length > 0) {
    return { ok: true, quote: b.quote }
  }
  const kind = shipErrorKindFromResponse(status, b)
  return {
    ok: false,
    kind,
    ...(b?.minParcels != null ? { minParcels: b.minParcels } : {}),
  }
}

/**
 * El carrito cambió después de cotizar ⇒ el quote local ya no corresponde al
 * pedido (Fase 1b: el fee depende de peso/bultos, a diferencia de delivery).
 * El servidor lo rechazaría igual por fingerprint (`/api/online-orders/session`,
 * I2), pero la UX no debe llegar ahí — se invalida ANTES, en el carrito.
 */
export function shipQuoteInvalidatedByCartChange(
  quotedForFingerprint: string | null,
  currentCart: CartLineLike[],
): boolean {
  if (quotedForFingerprint === null) return false
  return quotedForFingerprint !== shipCartFingerprint(currentCart)
}

/**
 * G4 (`/api/online-orders/session`, ronda 5 P1-4): sin un `homeState`
 * conocido no hay forma de confirmar "mismo estado", así que la única lectura
 * segura es tratar el envío como FUERA de estado y no cobrar impuesto — el
 * servidor rechaza con 422 `tax_mismatch` si el cliente manda `taxCents > 0`
 * en ese caso. Esta función es la MISMA regla, calculada client-side, para
 * que el total que el cliente ve (y lo que `/session` recibe en `taxCents`)
 * jamás diverja de lo que el servidor va a exigir.
 */
export function shipTaxMustBeZero(
  homeState: string | null | undefined,
  destState: string | null | undefined,
): boolean {
  const home = (homeState ?? "").trim().toUpperCase()
  const dest = (destState ?? "").trim().toUpperCase()
  return !home || dest !== home
}

/**
 * ¿El sheet debe degradar el modo "ship" a "pickup" AHORA MISMO?
 *
 * Ronda de fix 1, CRITICAL: extraído de `OrderSheet.tsx` para poder probarlo
 * sin jsdom. El bug real que esto existe para blindar: `PaymentStep.onSuccess`
 * hace `setCart([])` + `setStep("confirm")` en el MISMO batch — el carrito
 * vacío hace que `shippingAvailable` (que SÍ depende del carrito, a diferencia
 * de `deliveryAvailable`) calcule `false`. Sin el guard de `step`, este
 * predicado decía "degradar" y el efecto que lo consume revertía el
 * `setStep("confirm")` de vuelta a "cart" — el cliente que ACABA de pagar
 * veía el carrito vacío en vez de su confirmación (perdía el número de orden,
 * el Wallet pass y el link del recibo). `step === "confirm"` es SIEMPRE
 * `false` acá, sin excepción — mismo criterio que el efecto preexistente que
 * vacía el carrito a mitad de flujo (`OrderSheet.tsx` ~254).
 */
export function shipModeShouldDegradeToPickup(args: {
  step: string
  deliveryMode: string
  shippingAvailable: boolean
}): boolean {
  if (args.step === "confirm") return false
  return args.deliveryMode === "ship" && !args.shippingAvailable
}

/**
 * ¿`ShipAddressStep` debe disparar una cotización automática AHORA?
 *
 * Ronda de fix 1, IMPORTANT-4: el caso que esto arregla — el cliente ya
 * cotizó (dirección + nombre completos), vuelve al carrito, cambia algo, y
 * `OrderSheet` invalida `shipQuote` (ver `shipQuoteInvalidatedByCartChange`)
 * pero CONSERVA `shipTo` (dirección + nombre siguen ahí — no hace falta
 * volver a tipearlos). Al volver al paso de dirección, el componente
 * REMONTA con `state` inicializado en `'idle'` (porque `shipQuote` es null)
 * pero con el formulario YA lleno: sin este chequeo, cotizar solo se
 * disparaba al elegir una sugerencia NUEVA o al salir del campo de nombre —
 * el CTA quedaba deshabilitado sin explicación ni salida obvia.
 */
export function shipAddressReadyForAutoQuote(args: {
  hasSelectedAddress: boolean
  recipientName: string
  quoteState: string
  hasActiveQuote: boolean
}): boolean {
  return (
    args.hasSelectedAddress &&
    args.recipientName.trim().length > 0 &&
    args.quoteState === "idle" &&
    !args.hasActiveQuote
  )
}
